// ----------------------------------------------------------------------------
// Component: Tab
// ----------------------------------------------------------------------------
//
// TAB component with desktop tab functionality.
// With blockification: nodes are moved from .tabs--block-sources into place before init.
//
// ----------------------------------------------------------------------------

import Swiper from 'swiper';
import { Navigation, A11y, Keyboard } from 'swiper/modules';
import 'snippets/_containers/css-grid/css-grid';
import 'snippets/icon/icon';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/a11y';
import 'swiper/css/keyboard';
import './_tabbed-content.scss';
var TAB_SECTION = '.section--tabs';
var BLOCK_SOURCES = '.tabs--block-sources';
var BLOCK_SOURCE_ITEM = '.tab__block-source';
var TAB_ACCORDION = '.js-tabs--accordion';
var TAB_ITEM_ACCORDION = '.js-tab__item--accordion';
var TAB_TITLE_ACCORDION = '.js-tab__title--accordion';
var TAB_BUTTON_ACCORDION = '.js-tab__button--accordion';
var TAB_CONTENT_ACCORDION = '.js-tab__content--accordion';
var TAB_HORIZONTAL = '.js-tabs--horizontal';
var TAB_BUTTON_HORIZONTAL = '.js-tab__button--horizontal';
var TAB_CONTENT_HORIZONTAL = '.js-tab__content--horizontal';
var OPEN_CLASS = 'is-open';
var OPEN_CLASS_SELECTOR = '.is-open';
var SWIPER_CLASS = 'swiper--tabs';
var SWIPER_CLASS_SELECTOR = '.swiper--tabs';
var SWIPER_NAV_PREV_CLASS = 'swiper-button-prev--tabs';
var SWIPER_NAV_PREV_CLASS_SELECTOR = '.swiper-button-prev--tabs';
var SWIPER_NAV_NEXT_CLASS = 'swiper-button-next--tabs';
var SWIPER_NAV_NEXT_CLASS_SELECTOR = '.swiper-button-next--tabs';
var HORIZONTAL_KEYS = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
var ACCORDION_KEYS = ['ArrowUp', 'ArrowDown', 'Home', 'End'];
function reorganizeBlockifiedTabs(tabSection) {
  var sources = tabSection.querySelector(BLOCK_SOURCES);
  if (!sources) return;
  var horizontalTabs = tabSection.querySelector(TAB_HORIZONTAL);
  var accordionTabs = tabSection.querySelector(TAB_ACCORDION);
  var listEl = horizontalTabs && horizontalTabs.querySelector('.tab__list--horizontal');
  var items = sources.querySelectorAll(BLOCK_SOURCE_ITEM);
  items.forEach(function (wrapper, index) {
    var button = wrapper.querySelector(TAB_BUTTON_HORIZONTAL);
    var panel = wrapper.querySelector(TAB_CONTENT_HORIZONTAL);
    var accordionItem = wrapper.querySelector(TAB_ITEM_ACCORDION);
    if (listEl && button) {
      if (index === 0) {
        button.setAttribute('aria-selected', 'true');
        button.setAttribute('tabindex', '0');
      }
      listEl.appendChild(button);
    }
    if (horizontalTabs && panel) {
      horizontalTabs.appendChild(panel);
    }
    if (accordionTabs && accordionItem) {
      accordionTabs.appendChild(accordionItem);
    }
  });
  sources.remove();
}
function initHorizontalTabs(buttons, content) {
  buttons.forEach(function (button) {
    button.addEventListener('click', function (event) {
      event.preventDefault();
      updateHorizontalTabs(event.target, buttons, content);
    });
    button.addEventListener('keydown', function (event) {
      handleKeyboard('horizontal', event, buttons, content);
    });
  });
}
function updateHorizontalTabs(button, allButtons, allContent) {
  var content = document.getElementById(button.getAttribute('aria-controls'));
  if (!button.classList.contains(OPEN_CLASS)) {
    updateAttributeForElements(allButtons, 'aria-selected', 'false');
    updateAttributeForElements(allButtons, 'tabindex', '-1');
    removeClassFromElements(allButtons, OPEN_CLASS);
    removeClassFromElements(allContent, OPEN_CLASS);
    openHorizontalTab(button, content);
  }
}
function openHorizontalTab(button, content) {
  button.classList.add(OPEN_CLASS);
  button.setAttribute('aria-selected', 'true');
  button.setAttribute('tabindex', '0');
  content.classList.add(OPEN_CLASS);
}
function initAccordionTabs(buttons) {
  buttons.forEach(function (button) {
    button.addEventListener('click', function (event) {
      event.preventDefault();
      updateAccordionTabs(event.target);
    });
    button.addEventListener('keydown', function (event) {
      handleKeyboard('accordion', event, buttons, null);
    });
  });
}
function updateAccordionTabs(button) {
  var tab = generateTab(button);
  var tabs = tab.$item.parentNode;
  var openClassElems = tabs.querySelectorAll(OPEN_CLASS_SELECTOR);
  var accordionContentItems = tabs.querySelectorAll(TAB_CONTENT_ACCORDION);
  var accordionButtons = tabs.querySelectorAll(TAB_BUTTON_ACCORDION);
  removeClassFromElements(openClassElems, OPEN_CLASS);
  updateStyleForElements(accordionContentItems, 'overflow', 'hidden');
  updateAttributeForElements(accordionButtons, 'aria-expanded', 'false');
  accordionContentItems.forEach(function (contentItem) {
    var itemLinks = contentItem.querySelectorAll('a');
    updateAttributeForElements(itemLinks, 'tabindex', '-1');
  });
  if (tab.$isOpening) {
    openAccordionTab(tab);
  }
}
function openAccordionTab(tab) {
  tab.$item.classList.add(OPEN_CLASS);
  tab.$button.classList.add(OPEN_CLASS);
  tab.$content.classList.add(OPEN_CLASS);
  tab.$button.setAttribute('aria-expanded', 'true');
  tab.$content.setAttribute('hidden', false);
  var contentItemLinks = tab.$content.querySelectorAll('a');
  updateAttributeForElements(contentItemLinks, 'tabindex', null);
  setTimeout(function () {
    tab.$content.style.overflow = 'visible';
  }, 200);
}
function generateTab(button) {
  var tab = {
    $container: button.closest(TAB_ACCORDION),
    $item: button.closest(TAB_ITEM_ACCORDION),
    $title: button.closest(TAB_TITLE_ACCORDION),
    $button: button,
    $content: button.closest(TAB_ITEM_ACCORDION).querySelector(TAB_CONTENT_ACCORDION),
    $isOpening: !button.classList.contains(OPEN_CLASS)
  };
  return tab;
}
function handleKeyboard(source, event, buttons, content) {
  var $this = event.target;
  var key = event.key;
  var sourceKeys = source === 'horizontal' ? HORIZONTAL_KEYS : ACCORDION_KEYS;
  if (sourceKeys.includes(key)) {
    event.preventDefault();
    var index;
    var length = buttons.length;
    var newIndex;
    for (var i = 0; i < length; i++) {
      if (buttons[i] === $this) {
        index = i;
        break;
      }
    }
    switch (key) {
      case 'ArrowUp':
        newIndex = (index + length - 1) % length;
        buttons[newIndex].focus();
        break;
      case 'ArrowDown':
        newIndex = (index + length + 1) % length;
        buttons[newIndex].focus();
        break;
      case 'ArrowLeft':
        newIndex = (index + length - 1) % length;
        buttons[newIndex].focus();
        updateHorizontalTabs(buttons[newIndex], buttons, content);
        break;
      case 'ArrowRight':
        newIndex = (index + length + 1) % length;
        buttons[newIndex].focus();
        updateHorizontalTabs(buttons[newIndex], buttons, content);
        break;
      case 'Home':
        buttons[0].focus();
        if (source === 'horizontal') {
          updateHorizontalTabs(buttons[0], buttons, content);
        }
        break;
      case 'End':
        newIndex = (length - 1) % length;
        buttons[newIndex].focus();
        if (source === 'horizontal') {
          updateHorizontalTabs(buttons[newIndex], buttons, content);
        }
        break;
    }
  }
}
function updateAttributeForElements(elems, attribute, value) {
  elems.forEach(function (elem) {
    elem.setAttribute(attribute, value);
  });
}
function removeClassFromElements(elems, className) {
  elems.forEach(function (elem) {
    elem.classList.remove(className);
  });
}
function updateStyleForElements(elems, style, value) {
  elems.forEach(function (elem) {
    elem.style[style] = value;
  });
}
function initTabSection(tabSection, index) {
  reorganizeBlockifiedTabs(tabSection);
  var horizontalTabs = tabSection.querySelector(TAB_HORIZONTAL);
  var horizontalTabButtons = horizontalTabs ? horizontalTabs.querySelectorAll(TAB_BUTTON_HORIZONTAL) : [];
  var horizontalTabContent = tabSection.querySelectorAll(TAB_CONTENT_HORIZONTAL);
  var accordionTabs = tabSection.querySelector(TAB_ACCORDION);
  if (horizontalTabButtons.length === 0) {
    // When the collection-list with tabs renders a single tab without header buttons,
    // force the first panel open so content is visible.
    if ((tabSection.classList.contains('collection-list--tabbed') || tabSection.classList.contains('collection-list--category-driver')) && horizontalTabContent.length > 0) {
      horizontalTabContent[0].classList.add(OPEN_CLASS);
    }
    return;
  }
  updateAttributeForElements(horizontalTabButtons, 'tabindex', '-1');
  openHorizontalTab(horizontalTabButtons[0], horizontalTabContent[0]);
  initHorizontalTabs(horizontalTabButtons, horizontalTabContent);
  if (accordionTabs !== null) {
    var accordionTabContentItemLinks = tabSection.querySelectorAll(TAB_CONTENT_ACCORDION + ' a');
    var accordionTabButtons = tabSection.querySelectorAll(TAB_BUTTON_ACCORDION);
    var tab = generateTab(accordionTabButtons[0]);
    updateAttributeForElements(accordionTabContentItemLinks, 'tabindex', '-1');
    openAccordionTab(tab);
    initAccordionTabs(accordionTabButtons);
  } else {
    var swiperContainer = tabSection.querySelector(SWIPER_CLASS_SELECTOR);
    if (swiperContainer) {
      var swiperPrevButton = tabSection.querySelector(SWIPER_NAV_PREV_CLASS_SELECTOR);
      var swiperNextButton = tabSection.querySelector(SWIPER_NAV_NEXT_CLASS_SELECTOR);
      var mobileBlockCount = swiperContainer.dataset.blockCount > 2 ? 2.1 : swiperContainer.dataset.blockCount;
      var blockCount = swiperContainer.dataset.blockCount;
      swiperContainer.classList.add(SWIPER_CLASS + '-' + index);
      swiperPrevButton === null || swiperPrevButton === void 0 || swiperPrevButton.classList.add(SWIPER_NAV_PREV_CLASS + '-' + index);
      swiperNextButton === null || swiperNextButton === void 0 || swiperNextButton.classList.add(SWIPER_NAV_NEXT_CLASS + '-' + index);
      var swiperConfig = {
        modules: [Navigation, A11y, Keyboard],
        slidesPerView: mobileBlockCount,
        spaceBetween: 0,
        autoHeight: true,
        observer: true,
        observeParents: true,
        a11y: false,
        watchOverflow: true,
        breakpoints: {
          768: {
            slidesPerView: blockCount
          }
        }
      };
      if (swiperPrevButton && swiperNextButton) {
        swiperConfig.navigation = {
          prevEl: swiperPrevButton,
          nextEl: swiperNextButton
        };
      }
      new Swiper(swiperContainer, swiperConfig);
    }
  }
}
function mountTabsSections() {
  document.querySelectorAll(TAB_SECTION).forEach(function (tabSection, index) {
    initTabSection(tabSection, index);
  });
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountTabsSections);
} else {
  mountTabsSections();
}
document.addEventListener('shopify:section:load', function (event) {
  var section = event.target;
  if (section.matches && section.matches(TAB_SECTION)) {
    initTabSection(section, 0);
  }
});